/**
 * Currency and number formatting utilities for Indian locale
 */

/**
 * Format a number using Indian numbering system (lakhs, crores)
 * @param {number} amount - The amount to format
 * @param {boolean} includeSymbol - Whether to include ₹ symbol (default: true)
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted amount string
 * 
 * @example
 * formatIndianCurrency(186000) // "₹1,86,000"
 * formatIndianCurrency(2500000) // "₹25,00,000"
 * formatIndianCurrency(1234567890) // "₹1,23,45,67,890"
 */
export const formatIndianCurrency = (amount, includeSymbol = true, decimals = 0) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return includeSymbol ? '₹0' : '0';
  }

  const num = Number(amount);
  
  // Handle negative numbers
  const isNegative = num < 0;
  const absNum = Math.abs(num);
  
  // Round to specified decimals
  const roundedNum = decimals > 0 
    ? absNum.toFixed(decimals) 
    : Math.round(absNum).toString();
  
  // Split into integer and decimal parts
  const [intPart, decPart] = roundedNum.split('.');
  
  // Format the integer part with Indian grouping
  // First group of 3, then groups of 2
  let formatted = '';
  const len = intPart.length;
  
  if (len <= 3) {
    formatted = intPart;
  } else {
    // Last 3 digits
    formatted = intPart.slice(-3);
    let remaining = intPart.slice(0, -3);
    
    // Add groups of 2
    while (remaining.length > 0) {
      const chunk = remaining.slice(-2);
      remaining = remaining.slice(0, -2);
      formatted = chunk + ',' + formatted;
    }
  }
  
  // Add decimal part if present
  if (decPart) {
    formatted += '.' + decPart;
  }
  
  // Add sign and symbol
  const sign = isNegative ? '-' : '';
  const symbol = includeSymbol ? '₹' : '';
  
  return `${sign}${symbol}${formatted}`;
};

/**
 * Format a number with Indian grouping (without currency symbol)
 * @param {number} num - The number to format
 * @returns {string} Formatted number string
 */
export const formatIndianNumber = (num) => {
  return formatIndianCurrency(num, false, 0);
};

/**
 * Format currency with compact notation (K, L, Cr)
 * @param {number} amount - The amount to format
 * @returns {string} Compact formatted string
 * 
 * @example
 * formatCompactIndian(50000) // "₹50K"
 * formatCompactIndian(500000) // "₹5L"
 * formatCompactIndian(10000000) // "₹1Cr"
 */
export const formatCompactIndian = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '₹0';
  }

  const num = Number(amount);
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 10000000) {
    // Crores (1 Cr = 10,000,000)
    return `${sign}₹${(absNum / 10000000).toFixed(1).replace(/\.0$/, '')}Cr`;
  } else if (absNum >= 100000) {
    // Lakhs (1 L = 100,000)
    return `${sign}₹${(absNum / 100000).toFixed(1).replace(/\.0$/, '')}L`;
  } else if (absNum >= 1000) {
    // Thousands
    return `${sign}₹${(absNum / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  
  return `${sign}₹${Math.round(absNum)}`;
};

/**
 * Parse an Indian formatted currency string back to number
 * @param {string} str - The formatted string
 * @returns {number} The numeric value
 */
export const parseIndianCurrency = (str) => {
  if (!str) return 0;
  // Remove currency symbol and commas
  const cleaned = str.toString().replace(/[₹,\s]/g, '');
  return parseFloat(cleaned) || 0;
};

/**
 * Format percentage with % symbol
 * @param {number} value - The percentage value
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  return `${Number(value).toFixed(decimals)}%`;
};

/**
 * Format GST amount with slab info
 * @param {number} baseAmount - The base amount
 * @param {number} gstSlab - The GST percentage (e.g., 12, 18)
 * @returns {object} Object with base, gst, and total amounts
 */
export const calculateGST = (baseAmount, gstSlab = 12) => {
  const base = Number(baseAmount) || 0;
  const gstPercent = Number(gstSlab) || 0;
  const gstAmount = base * (gstPercent / 100);
  const total = base + gstAmount;
  
  return {
    base: formatIndianCurrency(base),
    gstAmount: formatIndianCurrency(gstAmount),
    gstPercent: `${gstPercent}%`,
    total: formatIndianCurrency(total),
    baseNum: base,
    gstAmountNum: gstAmount,
    totalNum: total
  };
};

export default {
  formatIndianCurrency,
  formatIndianNumber,
  formatCompactIndian,
  parseIndianCurrency,
  formatPercentage,
  calculateGST
};
